import { useId, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useActiveTrip } from '../hooks/useActiveTrip';
import { useSiteIntel, siteIntelQueryKey } from '../hooks/useSiteIntel';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toaster';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { supabase } from '../lib/supabase';
import { persistOrQueue, queuedMessage } from '../lib/mutations';
import { formatSolutionType, timeAgo } from '../lib/formatters';
import { INTEL_MIN_CHARS, INTEL_PRIORITIES, INTEL_TAGS, SOLUTION_TYPES, type IntelPriority, type SolutionType } from '../lib/solutions';

export function IntelPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const { profileId } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const locationId = trip?.location?.id;
  const intelQuery = useSiteIntel(locationId);

  const [observation, setObservation] = useState('');
  const [solutionType, setSolutionType] = useState<SolutionType>(SOLUTION_TYPES[0]);
  const [priority, setPriority] = useState<IntelPriority>('medium');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const observationId = useId();
  const solutionId = useId();
  const priorityId = useId();

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  const trimmed = observation.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < INTEL_MIN_CHARS;
  const canSubmit = !submitting && trimmed.length >= INTEL_MIN_CHARS && Boolean(locationId);

  const toggleTag = (t: string) => {
    setSelectedTags((s) => {
      const n = new Set(s);
      if (n.has(t)) n.delete(t);
      else n.add(t);
      return n;
    });
  };

  const submit = async () => {
    if (!locationId || trimmed.length < INTEL_MIN_CHARS) return;
    if (!profileId) {
      notify('Your staff profile could not be resolved, so this observation cannot be attributed. Sign out and back in, then try again.', 'error');
      return;
    }
    setSubmitting(true);
    const payload = {
      location_id: locationId,
      trip_id: trip.id,
      observation: trimmed,
      solution_type: solutionType,
      priority,
      tags: Array.from(selectedTags),
      is_confidential: true,
      captured_by_user_id: profileId,
    };
    try {
      const outcome = await persistOrQueue('site_intel_insert', payload, () =>
        supabase.from('site_intel').insert(payload).select('id').single(),
      );
      if (outcome.status === 'failed') {
        notify(`Could not save the observation: ${outcome.message}`, 'error');
        return;
      }
      if (outcome.status === 'queued') notify(queuedMessage('The observation', outcome), 'warning');
      else {
        notify('Observation saved.', 'success');
        void qc.invalidateQueries({ queryKey: siteIntelQueryKey(locationId) });
      }
      setObservation('');
      setSelectedTags(new Set());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="rounded-lg bg-primary px-4 py-3">
        <h1 className="text-xl font-bold text-text-inverse">Site intel</h1>
        <p className="text-xs text-text-inverse/80">Internal only — confidential observations for {trip.location?.name ?? trip.customer?.name ?? trip.project_number}.</p>
      </div>

      <section className="space-y-4 rounded-lg border-l-4 border-accent-purple bg-accent-purple-soft p-4" aria-labelledby="add-observation">
        <h2 id="add-observation" className="text-xs font-semibold uppercase tracking-wider text-accent-purple">
          Add observation
        </h2>

        <div>
          <label htmlFor={observationId} className="input-label">
            Observation <span className="font-normal text-text-faint">(at least {INTEL_MIN_CHARS} characters)</span>
          </label>
          <textarea
            id={observationId}
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            rows={4}
            className="input-field"
            aria-invalid={tooShort}
            aria-describedby={tooShort ? `${observationId}-error` : undefined}
          />
          {tooShort && (
            <span id={`${observationId}-error`} className="input-error" role="status">
              {INTEL_MIN_CHARS - trimmed.length} more characters needed
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={solutionId} className="input-label">
              Solution type
            </label>
            <select id={solutionId} value={solutionType} onChange={(e) => setSolutionType(e.target.value as SolutionType)} className="input-field">
              {SOLUTION_TYPES.map((s) => (
                <option key={s} value={s}>
                  {formatSolutionType(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={priorityId} className="input-label">
              Priority
            </label>
            <select id={priorityId} value={priority} onChange={(e) => setPriority(e.target.value as IntelPriority)} className="input-field">
              {INTEL_PRIORITIES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <fieldset>
          <legend className="input-label mb-2">Tags</legend>
          <div className="flex flex-wrap gap-2">
            {INTEL_TAGS.map((t) => {
              const on = selectedTags.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleTag(t)}
                  className={`min-h-[40px] rounded-full px-3 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    on ? 'bg-primary text-text-inverse' : 'border border-border bg-surface text-text'
                  }`}
                >
                  {t.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </fieldset>

        <button type="button" onClick={() => void submit()} disabled={!canSubmit} className="btn-primary w-full">
          {submitting ? 'Saving…' : 'Save observation'}
        </button>
      </section>

      <section aria-labelledby="existing-intel">
        <h2 id="existing-intel" className="card-title mb-2">
          Recorded observations
        </h2>
        {intelQuery.isLoading ? (
          <LoadingBlock label="Loading observations…" />
        ) : intelQuery.isError ? (
          <ErrorBlock title="Could not load observations" error={intelQuery.error} onRetry={() => void intelQuery.refetch()} />
        ) : (intelQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            }
            title="No observations yet"
            subtitle="Nothing has been recorded for this location. Observations you add above appear here."
          />
        ) : (
          <ul className="space-y-3">
            {(intelQuery.data ?? []).map((item) => (
              <li key={item.id} className="card border-l-4 border-l-accent-purple p-4">
                <p className="whitespace-pre-wrap text-sm text-text">{item.observation}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {item.solution_type && (
                    <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">{formatSolutionType(item.solution_type)}</span>
                  )}
                  {item.priority && <StatusBadge status={item.priority} />}
                  {(item.tags ?? []).map((t) => (
                    <span key={t} className="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] text-text-muted">
                      {t.replace(/_/g, ' ')}
                    </span>
                  ))}
                  {item.estimate && (
                    <span className="rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-medium text-success">
                      Estimate {item.estimate.estimate_number ?? item.estimate.id.slice(0, 8)}
                      {item.estimate.status ? ` · ${item.estimate.status.replace(/_/g, ' ')}` : ''}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-text-faint">
                  {item.captured_by?.full_name ?? 'Unattributed'} · {timeAgo(item.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
